import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Phone, Globe, Instagram, Clock, Star, Heart } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { TID } from "@/constants/testIds";
import { PhotoGallery } from "@/components/PhotoGallery";

export default function BusinessDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [biz, setBiz] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get(`/businesses/${slug}`).then((r) => setBiz(r.data)).catch(() => nav("/directory"));
  }, [slug, nav]);

  const fav = async () => {
    if (!user) return nav("/login");
    try {
      await api.post("/favorites", { item_type: "business", item_id: biz.id });
      toast.success("Saved to favourites");
    } catch { toast.error("Couldn't save"); }
  };

  if (!biz) return <div className="p-20 text-center">Loading…</div>;

  return (
    <div>
      <div className="relative h-[55vh] min-h-[380px] overflow-hidden">
        <img src={biz.image} alt={biz.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-scrim" />
        <button onClick={() => nav(-1)} className="absolute top-6 left-6 btn-pill btn-outline !border-white/40 !text-white bg-black/20 backdrop-blur">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="absolute bottom-8 left-0 right-0 px-6 lg:px-10">
          <div className="max-w-[1200px] mx-auto text-white">
            <div className="text-[0.7rem] uppercase tracking-[0.22em] opacity-80 mb-2">{biz.category}</div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-display text-5xl md:text-7xl leading-none tracking-tight">
              {biz.name}
            </motion.h1>
            <div className="mt-4 flex flex-wrap gap-4 text-sm">
              <span className="inline-flex items-center gap-1.5"><Star size={14} className="fill-white" /> {biz.rating} · {biz.review_count} reviews</span>
              <span>{biz.price_range}</span>
              <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {biz.suburb}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="prose-heygc">
            <p>{biz.description}</p>
            {biz.tags?.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">{biz.tags.map((t) => <span key={t} className="chip">{t}</span>)}</div>
            )}
          </div>
          <PhotoGallery images={biz.gallery} title={biz.name} />
        </div>
        <aside className="lg:col-span-5">
          <div className="bg-white rounded-3xl border border-[color:var(--forest-line)] p-8">
            <h3 className="font-display text-2xl mb-6">Get in touch</h3>
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3"><MapPin size={15} className="mt-0.5 text-[color:var(--forest-2)]" /><span>{biz.address}</span></div>
              {biz.phone && <div className="flex items-start gap-3"><Phone size={15} className="mt-0.5 text-[color:var(--forest-2)]" /><a href={`tel:${biz.phone}`} className="link-editorial">{biz.phone}</a></div>}
              {biz.hours && <div className="flex items-start gap-3"><Clock size={15} className="mt-0.5 text-[color:var(--forest-2)]" /><span>{biz.hours}</span></div>}
              {biz.website && <div className="flex items-start gap-3"><Globe size={15} className="mt-0.5 text-[color:var(--forest-2)]" /><a href={biz.website} target="_blank" rel="noreferrer" className="link-editorial">Visit website</a></div>}
              {biz.instagram && <div className="flex items-start gap-3"><Instagram size={15} className="mt-0.5 text-[color:var(--forest-2)]" /><span>{biz.instagram}</span></div>}
            </div>
            <button onClick={fav} data-testid={TID.favBtn(biz.id)} className="mt-8 btn-pill btn-outline w-full">
              <Heart size={15} /> Save to favourites
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
